import { cn } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";

import {
  PRIORITY_LEVEL_ORDER,
  PRIORITY_LEVELS,
  type CampaignPriority,
  formPriorityLevelOf,
  priorityLevelOf,
} from "../constants/priority-levels";

interface PriorityLevelPickerProps {
  id: string;
  labelledBy: string;
  value: unknown;
  onChange: (value: CampaignPriority) => void;
  onBlur?: () => void;
  errorMessage?: string;
  disabled?: boolean;
}

export function PriorityLevelPicker({
  id,
  labelledBy,
  value,
  onChange,
  onBlur,
  errorMessage,
  disabled = false,
}: PriorityLevelPickerProps): JSX.Element {
  const selected: CampaignPriority = formPriorityLevelOf(value);
  const errorId = `${id}-error`;

  return (
    <div>
      <RadioGroup
        id={id}
        aria-labelledby={labelledBy}
        aria-invalid={Boolean(errorMessage)}
        aria-describedby={errorMessage ? errorId : undefined}
        value={String(selected)}
        onValueChange={(next: string) => onChange(priorityLevelOf(next))}
        onBlur={onBlur}
        disabled={disabled}
        className="grid-cols-1 sm:grid-cols-2 xl:grid-cols-4"
      >
        {PRIORITY_LEVEL_ORDER.map((level) => {
          const option = PRIORITY_LEVELS[level];
          const optionId = `${id}-${level}`;
          const nameId = `${optionId}-name`;
          const descriptionId = `${optionId}-description`;
          const isSelected = level === selected;
          return (
            <label
              key={level}
              htmlFor={optionId}
              data-state={isSelected ? "checked" : "unchecked"}
              className={cn(
                "flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
                "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2",
                isSelected ? option.selectedClassName : "border-input hover:bg-muted/50",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <RadioGroupItem
                id={optionId}
                value={String(level)}
                aria-labelledby={nameId}
                aria-describedby={descriptionId}
                className="mt-0.5 shrink-0"
              />
              <span className="flex flex-col gap-1">
                <span id={nameId} className="flex items-center gap-2 text-sm font-medium">
                  <span
                    aria-hidden="true"
                    className={cn("h-2.5 w-2.5 rounded-full", option.dotClassName)}
                  />
                  {option.label}
                </span>
                <span id={descriptionId} className="text-xs text-muted-foreground">
                  {option.description}
                </span>
              </span>
            </label>
          );
        })}
      </RadioGroup>
      {errorMessage && (
        <p id={errorId} className="mt-1 text-sm text-red-500">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
